import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";
import { resolveAssetUrl } from "@/lib/api";
import { Loader2 } from "lucide-react";

const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === "admin") nav("/admin");
      else nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0b0b0b] text-white flex items-center justify-center px-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-3">
          <Link to="/" className="inline-flex flex-col items-center space-y-3">
            <img
              src={resolveAssetUrl(BRAND.LOGO)}
              alt="Faith Athletic Clothing"
              className="h-24 w-24 object-contain"
            />
            <span className="text-[10px] tracking-[0.4em] text-zinc-400 uppercase">
              Admin Portal
            </span>
          </Link>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tight pt-4">
            Sign In
          </h1>
          <p className="text-xs text-zinc-500 tracking-widest uppercase">
            Admin & Staff Access
          </p>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 border border-zinc-800 bg-zinc-950 p-8"
          data-testid="login-form"
        >
          <div className="space-y-2">
            <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
              Email
            </label>
            <input
              required
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 text-sm focus:border-zinc-500 outline-none"
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
              Password
            </label>
            <input
              required
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 text-sm focus:border-zinc-500 outline-none"
              data-testid="login-password-input"
            />
          </div>
          {err && (
            <div
              className="text-red-400 text-xs tracking-wider"
              data-testid="login-error"
            >
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black font-heading font-bold text-sm tracking-widest uppercase py-4 hover:bg-zinc-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Signing in
              </>
            ) : (
              "Enter Dashboard"
            )}
          </button>
        </form>

        <p className="text-center text-[10px] text-zinc-600 tracking-widest uppercase">
          "I can do all things through Christ who strengthens me."
        </p>
      </div>
    </div>
  );
};

export default Login;
